import { atom } from 'jotai';
import { atomWithReset, atomWithStorage, atomFamily } from 'jotai/utils';
import type { Coordinate } from '~/components/browser/implementations/WebRtcVmView';

export const MIN_AI_CHAT_PANEL_WIDTH = 240;
export const MIN_NO_CODE_EDITOR_PANEL_WIDTH = 350;

export type Component = {
  name: string;
  stepIds: string[];
  testIds: string[];
};

export type ScreenshotDiff = {
  opened: boolean; // whether the screenshot diff view is open
  oldOperationId?: string;
  newOperationId?: string;
  // Direct screenshot URLs for the "right" (new) side of the comparison viewer.
  // Used when screenshots come from AI action logs instead of a stored operation.
  // Enables Before/Action/After tabs in the screenshot viewer.
  rightDirectScreenshots?: { before?: string; action?: string; after?: string };
};

export const browserClickPositionAtom = atom<{ x: Coordinate; y: Coordinate } | undefined>(undefined);

export const navbarOpenAtom = atom(true);
export const hideNavbarAtom = atomWithStorage('hideNavbar', false);
export const onTestPageAtom = atom(false);

// Map from stepId to the order of the new operation's previous operation in the step. Use -1 if there is no previous operation
export const stepIdToPrevOpOrderAtom = atom<{ stepId: string; prevOpOrder: number } | undefined>(undefined);

export const testStartingUrlAtom = atomWithReset<string | undefined>(undefined);

export const displayedTestIdAtom = atom<string | undefined>(undefined);

export type FixInfo = { type: string; id: string; name: string };

export const fixesFilesAtom = atom<FixInfo[]>([]);

// For the main editor panel
export const isCodeEditorViewAtom = atom<boolean>(false);

export const autoUserFlowRefreshAtom = atom<number>(0);

// Command menu
export const commandMenuOpenedAtom = atom<boolean>(false);

export const previewExecutionIdAtom = atom<string | undefined>(undefined);

// Whether a step's previous version is stored in temp storage.
// This usually means we can now safely cancel the step and be able to revert.
// Keyed by stepId_previewExecutionId
export const prevStepStoredInTempFamily = atomFamily((_key: string) => atom<boolean | undefined>(undefined));

// This atom will store the list of keys that have been used.
// Used for cleaning up other atoms
export const prevStepKeysAtom = atom<string[]>([]);

// For screenshot viewer in diff view
export const screenshotDiffAtom = atomWithReset<ScreenshotDiff>({ opened: false });

// For test draft view split panes
export const noCodeEditorPanelWidthAtom = atomWithStorage<number>(
  'stably-no-code-editor-panel-width',
  MIN_NO_CODE_EDITOR_PANEL_WIDTH + 40,
);
export const rightPanelWidthAtom = atomWithStorage<number>('stably-right-panel-width', MIN_AI_CHAT_PANEL_WIDTH);

// Console panel collapsed state
export const consolePanelCollapsedAtom = atomWithStorage<boolean>('stably-console-panel-collapsed', true);

// Console panel height
export const consolePanelHeightAtom = atomWithStorage<number>('stably-console-panel-height', 200);

// Console panel tab type
export type ConsolePanelTab = 'console' | 'variables' | 'network' | 'mailbox' | 'admin' | 'settings';

// Console panel active tab - defaults to console
export const consolePanelActiveTabAtom = atomWithStorage<ConsolePanelTab>('stably-console-active-tab', 'variables');

// Lets admin toggle off the admin options (so we see what users see)
export const adminUiHiddenAtom = atomWithStorage<boolean>('admin-ui-hidden', false);

// Auto-heal accept changes loading state - keyed by testContentId
export const isAcceptingAutohealChangesAtom = atomFamily((_testContentId: string) => atom<boolean>(false));

export type PlaywrightTriageState =
  | {
      status: 'loading';
    }
  | {
      status: 'refreshing';
    }
  | {
      status: 'success';
    }
  | {
      status: 'error';
      errorMessage?: string;
    };

/**
 * View mode for switching between Classic and Agent interfaces.
 * Persisted in localStorage per org:
 * - stably-view-mode-org-<orgId>
 */
export type ViewMode = 'classic' | 'agent';
export const viewModeMemoryAtom = atom<ViewMode>('classic');
export const viewModeAtomFamily = atomFamily((scope: string) =>
  atomWithStorage<ViewMode>(`stably-view-mode-${scope}`, 'classic'),
);
